<!--
  Nav Modal, 520 x 580. The panel is as tall as its list, up to the frame's 580px, past which
  the list scrolls. Padding is 24px on every side (16px compact).
  -->
<div class="ic-acct-panel" [class.ic-acct-panel--compact]="compact()">

  <div class="ic-acct-panel__header">
    <h2 class="ic-acct-panel__title" [id]="titleId">{{ 'HEADER.ACCOUNTS' | translate }}</h2>

    <!--
      A bare numeral: no parentheses, no "of", 4px after the title and top-aligned to its
      cap height rather than the baseline. Counts top-level accounts, not rows.

      Hidden from assistive technology, and nothing is lost by hiding it. Read aloud it
      lands straight after the heading as "Accounts 5" - two announcements the listener has
      to join up, with nothing saying five of WHAT. The count is carried instead by
      `aria-setsize` on the top-level rows, where it arrives attached to the thing it
      counts: "Riverside Town Hall Park, 1 of 5". Same number, in the one place it means
      something.

      A keyed "{{ n }} accounts" beside the heading would be better still. It is enumerated
      on the stage-4 i18n delta and deliberately NOT implemented here: it is a new string,
      and en-us.json is frozen until the delta is approved.
      -->
    <span class="ic-acct-panel__count" aria-hidden="true">{{ accountCount() }}</span>

    <button type="button"
            class="ic-acct-panel__select-btn"
            (click)="accountSelectClicked.emit()">
      {{ 'MENUS.ACCOUNT_SELECT' | translate }}
    </button>
  </div>

  @if (isEmpty()) {
    <!-- The zero-accounts treatment is a page-level card on Home, not a panel state, so
      the panel only needs a way out of an empty list. Built in stage 4. -->
    <div class="ic-acct-panel__empty">
      <button type="button"
              class="ic-acct-panel__select-btn"
              (click)="createAccountClicked.emit()">
        {{ 'HEADER.CREATE_ACCOUNT' | translate }}
      </button>
    </div>
  } @else {
    <!--
      A TREE, not a listbox. `role="option"` may not contain interactive descendants and
      every row here has two buttons, so listbox was ruled out rather than passed over -
      the full reasoning is in the component's class doc. Named by the visible heading via
      `aria-labelledby` rather than by an `aria-label` holding a second copy of the same
      translated string, so the name cannot drift from the label on screen.

      Rendered FLAT - a parent's children are siblings, not wrapped in a `role="group"` -
      which is what keeps `rows` and `rowElements` a single array the keyboard model can
      index into. The price is that every row must carry `aria-level`, `aria-posinset` and
      `aria-setsize` by hand, and it pays it below.
      -->
    <!-- The scroller is borderless and the bordered list sits inside it, so the scrollbar is drawn on
      the panel beside the list's right border rather than inside the list. -->
    <div class="ic-acct-panel__scroller" icScrollbar="dark">
      <ul class="ic-acct-panel__list" role="tree" [attr.aria-labelledby]="titleId">
        @for (row of rows(); track trackByAccountId($index, row)) {
          <!--
            A locked row is aria-disabled rather than removed from the tab order: it has to
            stay reachable, or the one user who most needs to hear WHY it cannot be chosen
            is the one user who can never get to the badge that says so. aria-disabled is
            the announcement; the inertness is enforced in the container, which refuses the
            emitted id.

            `aria-expanded` is withheld from a main row with no children. The design draws
            the chevron on every main row whether or not there is anything under it, and a
            childless row that announces "collapsed" is promising an expansion that both
            Right-arrow and the chevron will decline to perform.

            Named by its account name alone, and described by its meta line, rather than
            letting either be computed from the row's contents - see `rowNameId`.
            -->
          <li class="ic-acct-panel__row"
              #rowElement
              [animate.enter]="settled() && row.kind === 'sub' ? 'ic-acct-panel__row--entering' : ''"
              [animate.leave]="row.kind === 'sub' ? 'ic-acct-panel__row--leaving' : ''"
              [class.ic-acct-panel__row--selected]="row.isSelected"
              [class.ic-acct-panel__row--sub]="row.kind === 'sub'"
              [class.ic-acct-panel__row--locked]="row.isLocked"
              role="treeitem"
              [attr.aria-level]="row.kind === 'main' ? 1 : 2"
              [attr.aria-posinset]="row.posInSet"
              [attr.aria-setsize]="row.setSize"
              [attr.tabindex]="row.isActive ? 0 : -1"
              [attr.aria-selected]="row.isSelected"
              [attr.aria-expanded]="row.kind === 'main' && row.hasChildren ? row.isExpanded : null"
              [attr.aria-disabled]="row.isLocked ? 'true' : null"
              [attr.aria-labelledby]="rowNameId(row)"
              [attr.aria-describedby]="rowDescribedBy(row)"
              (click)="onRowClick(row)"
              (focus)="onRowFocus(row, $event)"
              (keydown)="onRowKeydown(row, $index, $event)">

            <div class="ic-acct-panel__row-leading">
              <!-- The selected row's hierarchy glyph is replaced by the blue check, so
                the icon alone cannot tell you whether it is a main or a sub. -->
              @if (row.isSelected) {
                <span class="ic-acct-panel__glyph ic-acct-panel__glyph--check" aria-hidden="true">
                  <ic-icon-svg iconName="checkmark" [svgResizable]="true"></ic-icon-svg>
                </span>
              } @else {
                <!-- Still a globe on a sub row, because a sub-account IS an account - but a
                  reduced one behind a child-of elbow, so the row states its nesting on its own
                  rather than relying on the indent. The frame's folder-with-globe was dropped
                  because an account is not a folder, and a map pin because it says "a place". -->
                <span class="ic-acct-panel__glyph" aria-hidden="true">
                  <ic-icon-svg [iconName]="row.kind === 'sub' ? 'globe-sub' : 'globe'"
                               [svgResizable]="true"></ic-icon-svg>
                </span>
              }

              <div class="ic-acct-panel__row-text">
                <span class="ic-acct-panel__row-name" [id]="rowNameId(row)">{{ row.account.accountName }}</span>

                <span class="ic-acct-panel__row-meta" [id]="rowMetaId(row)">
                  @for (segment of row.metaSegments; track $index) {
                    <span class="ic-acct-panel__row-meta-segment">{{ segment }}</span>

                    <!-- The past-due badge sits directly after the account number,
                      so it is emitted between the first and second segments. -->
                    @if ($first && row.isLocked) {
                      <strong class="ic-acct-panel__row-badge">
                        {{ 'SPECIAL.BILL_PAST_DUE' | translate | uppercase }}
                      </strong>

                      <!--
                        A real anchor, not a button dressed as one: it carries an
                        href, so it is focusable, in the tab order, and openable in
                        a new tab like any other link. It is a sibling of the badge
                        rather than a child so the badge's red does not bleed into
                        it. Omitted entirely without the Billing permission -
                        offering a page the user cannot act on is worse than saying
                        nothing.
                        -->
                      <!-- Named per account for the same reason the two row
                        buttons are: a screen reader listing the links on
                        this panel otherwise reads "PAY NOW, PAY NOW, PAY
                        NOW" and the user has no way to tell which bill
                        they are about to go and pay. The visible "PAY NOW"
                        is preserved at the front of the accessible name,
                        which is what WCAG 2.5.3 asks for. -->
                      @if (row.canPayNow) {
                        <a class="ic-acct-panel__row-pay-now"
                           routerLink="/billing/balance"
                           [attr.tabindex]="row.isActive ? null : -1"
                           [attr.aria-disabled]="controlDisabledOverride(row)"
                           [attr.aria-label]="'STRINGS.ACCOUNT_PANEL_PAY_NOW_FOR' | translate:{ accountName: row.account.accountName }"
                           (click)="onPayNowClick(row, $event)">{{ 'SPECIAL.PAY_NOW' | translate | uppercase }}</a>
                      }
                    }

                    @if (!$last) {
                      <span class="ic-acct-panel__row-sep" aria-hidden="true">{{ metaSeparator }}</span>

                      <!--
                        The bullet is hidden because a screen reader would
                        otherwise say "bullet" three times per row. Hiding it
                        took the WHITESPACE with it: Chrome's computed
                        description for this row collapsed to
                        "#1566512134/52 Sub accounts", where the account
                        number and the controller count have fused into one
                        unreadable number. This comma is the separator the
                        hidden bullet used to be - inaudible as punctuation,
                        audible as a pause.
                        -->
                      <span class="ic-acct-panel__sr-only">, </span>
                    }
                  }
                </span>

                <!--
                  WHY the row cannot be chosen, not merely THAT it cannot.
                  `aria-disabled` on its own announces "dimmed" and leaves the user
                  to guess; the BILL PAST DUE badge above says what is wrong but not
                  what it costs them, and a sighted user gets to infer the link
                  between a red badge and a row that will not respond. This sentence
                  is that inference, spelled out, for the user who cannot see the
                  badge sitting next to the row they just failed to select.

                  Screen-reader-only rather than visible: the design draws no locked
                  treatment at all (transcription section 7), so adding visible copy
                  would be inventing UI. Adding an announcement is not.
                  -->
                @if (row.isLocked) {
                  <span class="ic-acct-panel__sr-only" [id]="rowLockedReasonId(row)">
                    {{ 'STRINGS.ACCOUNT_PANEL_LOCKED_REASON' | translate }}
                  </span>
                }
              </div>
            </div>

            <div class="ic-acct-panel__row-trailing">
              <!--
                The row's controls join the tab order only while their row holds the
                list's tab stop. Tab from a focused row therefore enters that row's
                controls and then leaves the list, instead of walking N rows x 2
                buttons - an unbounded tab order across a scrolling list is hostile to
                exactly the users this whole feature is for.

                Written as `null` rather than `0` so the active row's controls carry no
                tabindex at all: a button and an anchor-with-href are focusable on their
                own, and the attribute is only ever needed to take that away.
                -->
              <!--
                Named per account, not "Settings". Both trailing controls are
                icon-only, so without a name they announce as "button" and nothing
                else; with a generic name they announce identically on all N rows,
                which is worse, because a rotor listing the panel's buttons then
                reads "Settings, Settings, Settings" and the user cannot tell whose
                settings they are about to open. The account name is the whole point
                of the label.
                -->
              <button type="button"
                      class="ic-acct-panel__row-btn"
                      [attr.tabindex]="row.isActive ? null : -1"
                      [attr.aria-disabled]="controlDisabledOverride(row)"
                      [attr.aria-label]="'STRINGS.ACCOUNT_PANEL_SETTINGS_FOR' | translate:{ accountName: row.account.accountName }"
                      (click)="onSettingsClick(row, $event)">
                <span class="ic-acct-panel__glyph" aria-hidden="true">
                  <ic-menu-icons iconName="person-settings" [svgResizable]="true"></ic-menu-icons>
                </span>
              </button>

              <!-- Sub-account rows are terminal: only one level of nesting is designed,
                so they get the person-gear button and no chevron. -->
              @if (row.kind === 'main') {
                <!--
                  The label states the ACTION the press will perform - "Show" while
                  collapsed, "Hide" while expanded - rather than naming the control
                  after the glyph currently drawn in it. `aria-expanded` states the
                  current state alongside it, and is withheld on a row with no
                  children for the same reason the row withholds it: a control that
                  announces "collapsed" and then does nothing when pressed has
                  already lied once.

                  On a row with no children the chevron keeps its slot, so the trailing
                  column stays aligned down the list, but it is disabled and greyed. It
                  is `aria-disabled` rather than `disabled` so it stays focusable: a
                  screen-reader user can still find it and hear that it is unavailable.
                  The click handler is what makes it inert.
                  -->
                <button type="button"
                        class="ic-acct-panel__row-btn"
                        [class.ic-acct-panel__row-btn--disabled]="!row.hasChildren"
                        [attr.tabindex]="row.isActive ? null : -1"
                        [attr.aria-disabled]="chevronAriaDisabled(row)"
                        [attr.aria-expanded]="row.hasChildren ? row.isExpanded : null"
                        [attr.aria-label]="(row.isExpanded ? 'STRINGS.ACCOUNT_PANEL_COLLAPSE_FOR' : 'STRINGS.ACCOUNT_PANEL_EXPAND_FOR') | translate:{ accountName: row.account.accountName }"
                        (click)="onChevronClick(row, $event)">
                  <!-- Matches the nav panel: sidebar.component.html:62 shows sn-close-acc
                    (a chevron UP) on the expanded item and sn-open-acc (DOWN) on collapsed
                    ones, so up means "collapse me" and down means "open me". The Figma frame
                    has it the other way round; the shipped sidebar wins, because the two
                    controls sit in the same chrome and must not disagree. -->
                  <span class="ic-acct-panel__glyph" aria-hidden="true">
                    <ic-icon-svg [iconName]="row.isExpanded ? 'chevron-up' : 'chevron-down'"
                                 [svgResizable]="true"></ic-icon-svg>
                  </span>
                </button>
              }
            </div>
          </li>
        }
      </ul>
    </div>
  }
</div>
